import React, { useState, useEffect } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import OwnerDashboardMenu from './OwnerDashboardMenu';

/**
 * OnboardingDashboard - OWNER ONLY dashboard for tracking club onboarding metrics
 * 
 * Secret URL: /owner-9x7kq2m
 * Only accessible by authorized users
 */

const ALLOWED_UIDS = [
  'mygxGSMYBWZd9v02Xyew6WMtYe13', // Anthony
  'uM4lKlr7SjPGFzDK064F1Dk6Aco2'  // Additional authorized user
];

const OnboardingDashboard = () => {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [authorized, setAuthorized] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  
  const navigate = useNavigate();

  // Check authorization - wait for Firebase auth state
  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user || !ALLOWED_UIDS.includes(user.uid)) {
        // Silently redirect - don't reveal this page exists
        navigate('/');
        return;
      }
      
      setAuthorized(true);
      setAuthChecked(true);
    });
    
    return () => unsubscribe();
  }, [navigate]);

  const fetchMetrics = async () => {
    setLoading(true);
    setError(null);
    try {
      const functions = getFunctions();
      const getOnboardingMetrics = httpsCallable(functions, 'getOnboardingMetrics');
      const result = await getOnboardingMetrics();
      setMetrics(result.data);
      setLastRefresh(new Date());
    } catch (err) {
      console.error('Error fetching onboarding metrics:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authorized) {
      fetchMetrics();
    }
  }, [authorized]);

  if (!authChecked) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        fontSize: '18px',
        color: '#6b7280'
      }}>
        Loading...
      </div>
    );
  }

  const formatMinutes = (mins) => {
    if (mins === null || mins === undefined) return '—';
    if (mins < 60) return `${mins}m`;
    const hours = Math.floor(mins / 60);
    const remainingMins = mins % 60;
    if (hours < 24) return `${hours}h ${remainingMins}m`;
    const days = Math.floor(hours / 24);
    const remainingHours = hours % 24;
    return `${days}d ${remainingHours}h`;
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '—';
    let date;
    if (timestamp.toDate) {
      // Firestore Timestamp object
      date = timestamp.toDate();
    } else if (timestamp._seconds) {
      // Serialized timestamp from Cloud Function
      date = new Date(timestamp._seconds * 1000);
    } else if (timestamp.seconds) {
      // Alternative serialization format
      date = new Date(timestamp.seconds * 1000);
    } else {
      date = new Date(timestamp);
    }
    
    if (isNaN(date.getTime())) return '—';
    
    return date.toLocaleDateString('en-AU', { 
      day: 'numeric', 
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getProgressStatus = (club) => {
    if (club.firstAllocationAt) return { label: 'Complete', color: '#22c55e', step: 4 };
    if (club.firstTeamAt) return { label: 'Has Team', color: '#3b82f6', step: 3 };
    if (club.firstPitchAt) return { label: 'Has Pitch', color: '#f59e0b', step: 2 };
    return { label: 'Just Signed Up', color: '#ef4444', step: 1 };
  };

  const isStalled = (club) => {
    if (club.firstAllocationAt) return false;
    if (!club.signupAt) return false;
    
    let signupTime;
    if (club.signupAt.toDate) {
      signupTime = club.signupAt.toDate();
    } else if (club.signupAt._seconds) {
      signupTime = new Date(club.signupAt._seconds * 1000);
    } else if (club.signupAt.seconds) {
      signupTime = new Date(club.signupAt.seconds * 1000);
    } else {
      signupTime = new Date(club.signupAt);
    }
    
    if (isNaN(signupTime.getTime())) return false;
    
    const hoursSinceSignup = (Date.now() - signupTime.getTime()) / (1000 * 60 * 60);
    return hoursSinceSignup > 24;
  };

  if (!authorized) {
    return null; // Don't render anything while checking auth
  }

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingContainer}>
          <div style={styles.spinner}></div>
          <p style={styles.loadingText}>Loading onboarding metrics...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.errorContainer}>
          <span style={styles.errorIcon}>⚠️</span>
          <p style={styles.errorText}>Error: {error}</p>
          <button style={styles.retryButton} onClick={fetchMetrics}>Retry</button>
        </div>
      </div>
    );
  }

  const stalledClubs = metrics?.metrics?.filter(isStalled) || [];
  const completedClubs = metrics?.metrics?.filter(c => c.firstAllocationAt) || [];
  const inProgressClubs = metrics?.metrics?.filter(c => !c.firstAllocationAt && !isStalled(c)) || [];

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f1f5f9' }}>
      <OwnerDashboardMenu />
      <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Onboarding Dashboard</h1>
          <p style={styles.subtitle}>
            Track how quickly clubs get to their first allocation
          </p>
        </div>
        <div style={styles.headerActions}>
          <span style={styles.lastRefresh}>
            Last updated: {lastRefresh?.toLocaleTimeString()}
          </span>
          <button style={styles.refreshButton} onClick={fetchMetrics}>
            ↻ Refresh
          </button>
        </div>
      </div>

      {/* Key Metrics Cards */}
      <div style={styles.metricsGrid}>
        <div style={styles.metricCard}>
          <div style={styles.metricIcon}>🏟️</div>
          <div style={styles.metricValue}>{metrics?.totalClubs || 0}</div>
          <div style={styles.metricLabel}>Total Clubs</div>
        </div>
        
        <div style={styles.metricCard}>
          <div style={styles.metricIcon}>⚡</div>
          <div style={styles.metricValue}>
            {formatMinutes(metrics?.averages?.avgSignupToAllocationMinutes)}
          </div>
          <div style={styles.metricLabel}>Avg. Signup → Allocation</div>
          <div style={styles.metricSubtext}>
            {metrics?.counts?.withAllocation || 0} clubs completed
          </div>
        </div>

        <div style={styles.metricCard}>
          <div style={styles.metricIcon}>📍</div>
          <div style={styles.metricValue}>
            {formatMinutes(metrics?.averages?.avgSignupToPitchMinutes)}
          </div>
          <div style={styles.metricLabel}>Avg. Signup → First Pitch</div>
          <div style={styles.metricSubtext}>
            {metrics?.counts?.withPitch || 0} clubs with pitches
          </div>
        </div>

        <div style={styles.metricCard}>
          <div style={styles.metricIcon}>👥</div>
          <div style={styles.metricValue}>
            {formatMinutes(metrics?.averages?.avgSignupToTeamMinutes)}
          </div>
          <div style={styles.metricLabel}>Avg. Signup → First Team</div>
          <div style={styles.metricSubtext}>
            {metrics?.counts?.withTeam || 0} clubs with teams
          </div>
        </div>
      </div>

      {/* Funnel Visualization */}
      <div style={styles.funnelSection}>
        <h2 style={styles.sectionTitle}>Onboarding Funnel</h2>
        <div style={styles.funnel}>
          <FunnelBar 
            label="Signed Up" 
            count={metrics?.totalClubs || 0} 
            total={metrics?.totalClubs || 1}
            color="#243665"
          />
          <FunnelBar 
            label="Added Pitch" 
            count={metrics?.counts?.withPitch || 0} 
            total={metrics?.totalClubs || 1}
            color="#3b82f6"
          />
          <FunnelBar 
            label="Created Team" 
            count={metrics?.counts?.withTeam || 0} 
            total={metrics?.totalClubs || 1}
            color="#8b5cf6"
          />
          <FunnelBar 
            label="First Allocation" 
            count={metrics?.counts?.withAllocation || 0} 
            total={metrics?.totalClubs || 1}
            color="#22c55e"
          />
        </div>
      </div>

      {/* Stalled Clubs Alert */}
      {stalledClubs.length > 0 && (
        <div style={styles.alertSection}>
          <h2 style={styles.alertTitle}>
            ⚠️ {stalledClubs.length} Stalled Club{stalledClubs.length > 1 ? 's' : ''} (24h+ inactive)
          </h2>
          <div style={styles.clubList}>
            {stalledClubs.map((club) => (
              <ClubRow key={club.clubId} club={club} formatMinutes={formatMinutes} formatDate={formatDate} />
            ))}
          </div>
        </div>
      )}

      {/* In Progress */}
      {inProgressClubs.length > 0 && (
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>
            🔄 In Progress ({inProgressClubs.length})
          </h2>
          <div style={styles.clubList}>
            {inProgressClubs.map((club) => (
              <ClubRow key={club.clubId} club={club} formatMinutes={formatMinutes} formatDate={formatDate} />
            ))}
          </div>
        </div>
      )}

      {/* Completed */}
      {completedClubs.length > 0 && (
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>
            ✅ Completed Onboarding ({completedClubs.length})
          </h2>
          <div style={styles.clubList}>
            {completedClubs.map((club) => (
              <ClubRow key={club.clubId} club={club} formatMinutes={formatMinutes} formatDate={formatDate} />
            ))}
          </div>
        </div>
      )}

      {/* Empty State */}
      {metrics?.totalClubs === 0 && (
        <div style={styles.emptyState}>
          <div style={styles.emptyIcon}>📊</div>
          <h3 style={styles.emptyTitle}>No onboarding data yet</h3>
          <p style={styles.emptyText}>
            Metrics will appear here as new clubs sign up and go through the onboarding process.
          </p>
        </div>
      )}
      </div>
    </div>
  );
};

// Funnel Bar Component
const FunnelBar = ({ label, count, total, color }) => {
  const percentage = total > 0 ? (count / total) * 100 : 0;
  
  return (
    <div style={styles.funnelRow}>
      <div style={styles.funnelLabel}>{label}</div>
      <div style={styles.funnelBarContainer}>
        <div 
          style={{
            ...styles.funnelBarFill,
            width: `${percentage}%`,
            backgroundColor: color
          }}
        />
      </div>
      <div style={styles.funnelCount}>
        {count} <span style={styles.funnelPercentage}>({percentage.toFixed(0)}%)</span>
      </div>
    </div>
  );
};

// Club Row Component
const ClubRow = ({ club, formatMinutes, formatDate }) => {
  const [showEmailDraft, setShowEmailDraft] = useState(false);
  const [copied, setCopied] = useState(false);
  const status = getProgressStatus(club);
  const isLegacy = club.isLegacy || club.isBackfilled;
  
  // Calculate trial days remaining
  const getTrialDaysRemaining = () => {
    if (!club.trialEndsAt) return null;
    if (club.subscriptionStatus === 'active') return null;
    
    let trialEnd;
    if (club.trialEndsAt.toDate) {
      trialEnd = club.trialEndsAt.toDate();
    } else if (club.trialEndsAt._seconds) {
      trialEnd = new Date(club.trialEndsAt._seconds * 1000);
    } else if (club.trialEndsAt.seconds) {
      trialEnd = new Date(club.trialEndsAt.seconds * 1000);
    } else {
      trialEnd = new Date(club.trialEndsAt);
    }
    
    const now = new Date();
    const diffMs = trialEnd - now;
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
    return diffDays;
  };
  
  // Calculate days since signup
  const getDaysSinceSignup = () => {
    if (!club.signupAt) return 0;
    
    let signupDate;
    if (club.signupAt.toDate) {
      signupDate = club.signupAt.toDate();
    } else if (club.signupAt._seconds) {
      signupDate = new Date(club.signupAt._seconds * 1000);
    } else if (club.signupAt.seconds) {
      signupDate = new Date(club.signupAt.seconds * 1000);
    } else {
      signupDate = new Date(club.signupAt);
    }
    
    const now = new Date();
    const diffMs = now - signupDate;
    return Math.floor(diffMs / (1000 * 60 * 60 * 24));
  };
  
  // Get stuck step
  const getStuckStep = () => {
    if (!club.firstPitchAt) return 'adding your first pitch';
    if (!club.firstTeamAt) return 'creating your first team';
    if (!club.firstAllocationAt) return 'making your first allocation';
    return null;
  };
  
  const trialDays = getTrialDaysRemaining();
  const daysSinceSignup = getDaysSinceSignup();
  const stuckStep = getStuckStep();
  const firstName = club.ownerFirstName || 'there';
  
  // Customize benefits based on stuck step
  const getBenefits = () => {
    if (!club.firstPitchAt) {
      // Stuck on pitch
      return {
        plain: `✅ Visual satellite map - Draw your pitches exactly where they are
✅ Multiple pitch support - Manage all your facilities in one place
✅ Section configuration - Split pitches into training areas (A, B, C, D)
✅ Match day setup - Configure full-pitch allocations for game days`,
        html: `<ul style="padding-left: 20px; margin: 16px 0;">
          <li style="margin-bottom: 8px;">✅ <strong>Visual satellite map</strong> - Draw your pitches exactly where they are</li>
          <li style="margin-bottom: 8px;">✅ <strong>Multiple pitch support</strong> - Manage all your facilities in one place</li>
          <li style="margin-bottom: 8px;">✅ <strong>Section configuration</strong> - Split pitches into training areas (A, B, C, D)</li>
          <li style="margin-bottom: 8px;">✅ <strong>Match day setup</strong> - Configure full-pitch allocations for game days</li>
        </ul>`
      };
    }
    if (!club.firstTeamAt) {
      // Stuck on team
      return {
        plain: `✅ Color-coded teams - Instantly see which team is where on the grid
✅ Age group organisation - Keep U6s through seniors properly separated
✅ Quick allocation - Select team from dropdown when scheduling
✅ Team-based filtering - View the schedule for specific teams only
✅ Conflict prevention - System warns if a team is double-booked`,
        html: `<ul style="padding-left: 20px; margin: 16px 0;">
          <li style="margin-bottom: 8px;">✅ <strong>Color-coded teams</strong> - Instantly see which team is where on the grid</li>
          <li style="margin-bottom: 8px;">✅ <strong>Age group organisation</strong> - Keep U6s through seniors properly separated</li>
          <li style="margin-bottom: 8px;">✅ <strong>Quick allocation</strong> - Select team from dropdown when scheduling</li>
          <li style="margin-bottom: 8px;">✅ <strong>Team-based filtering</strong> - View the schedule for specific teams only</li>
          <li style="margin-bottom: 8px;">✅ <strong>Conflict prevention</strong> - System warns if a team is double-booked</li>
        </ul>`
      };
    }
    // Stuck on allocation
    return {
      plain: `✅ Visual allocation grid - See your entire week at a glance
✅ Recurring allocations - Set it once, schedule for the whole season
✅ Easy sharing - Send read-only schedules to coaches and parents
✅ Conflict prevention - No more double-bookings or scheduling headaches`,
      html: `<ul style="padding-left: 20px; margin: 16px 0;">
        <li style="margin-bottom: 8px;">✅ <strong>Visual allocation grid</strong> - See your entire week at a glance</li>
        <li style="margin-bottom: 8px;">✅ <strong>Recurring allocations</strong> - Set it once, schedule for the whole season</li>
        <li style="margin-bottom: 8px;">✅ <strong>Easy sharing</strong> - Send read-only schedules to coaches and parents</li>
        <li style="margin-bottom: 8px;">✅ <strong>Conflict prevention</strong> - No more double-bookings or scheduling headaches</li>
      </ul>`
    };
  };
  
  // Get the right link based on stuck step
  const getResumeLink = () => {
    if (!club.firstPitchAt) {
      return 'https://www.pitchallo.app/setup-wizard?step=pitch';
    }
    if (!club.firstTeamAt) {
      return 'https://www.pitchallo.app/setup-wizard?step=teams';
    }
    return 'https://www.pitchallo.app/menu';
  };
  
  const getLinkText = () => {
    if (!club.firstPitchAt) {
      return 'Continue setting up your pitches';
    }
    if (!club.firstTeamAt) {
      return 'Continue adding your teams';
    }
    return 'Go to your dashboard and create your first allocation';
  };
  
  const benefits = getBenefits();
  
  // Generate email draft (plain text for Gmail compose URL)
  const emailSubject = `Need help setting up ${club.clubName} on Pitchallo?`;
  const emailBodyPlain = `Hi ${firstName},

It's been ${daysSinceSignup} day${daysSinceSignup !== 1 ? 's' : ''} since you created your ${club.clubName} account on Pitchallo, and I noticed you haven't finished the setup yet.

It looks like you might be stuck on ${stuckStep}.

I'd love to help you get up and running! Completing this step unlocks:

${benefits.plain}

👉 ${getLinkText()}: ${getResumeLink()}

Or if you'd prefer, I can jump on a quick 10-minute call to walk you through it. Just reply to this email and let me know what time works for you.

Cheers,
Anthony
Pitchallo`;

  // HTML branded email
  const emailBodyHtml = `
<div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; max-width: 600px; margin: 0 auto;">
  <!-- Header -->
  <div style="background-color: #243665; padding: 24px; text-align: center; border-radius: 8px 8px 0 0;">
    <img src="https://www.pitchallo.app/logo-white.png" alt="Pitchallo" style="height: 40px;" onerror="this.style.display='none'"/>
    <div style="color: white; font-size: 24px; font-weight: bold; margin-top: 8px;">Pitchallo</div>
  </div>
  
  <!-- Body -->
  <div style="background-color: #ffffff; padding: 32px; border: 1px solid #e5e7eb; border-top: none;">
    <p style="font-size: 16px; color: #374151; margin-bottom: 16px;">Hi ${firstName},</p>
    
    <p style="font-size: 16px; color: #374151; margin-bottom: 16px;">
      It's been <strong>${daysSinceSignup} day${daysSinceSignup !== 1 ? 's' : ''}</strong> since you created your <strong>${club.clubName}</strong> account on Pitchallo, and I noticed you haven't finished the setup yet.
    </p>
    
    <p style="font-size: 16px; color: #374151; margin-bottom: 16px;">
      It looks like you might be stuck on <strong>${stuckStep}</strong>.
    </p>
    
    <p style="font-size: 16px; color: #374151; margin-bottom: 8px;">
      I'd love to help you get up and running! Completing this step unlocks:
    </p>
    
    ${benefits.html}
    
    <!-- CTA Button -->
    <div style="text-align: center; margin: 32px 0;">
      <a href="${getResumeLink()}" style="display: inline-block; background-color: #243665; color: white; padding: 14px 28px; text-decoration: none; border-radius: 8px; font-weight: 600; font-size: 16px;">
        ${getLinkText()}
      </a>
    </div>
    
    <p style="font-size: 16px; color: #374151; margin-bottom: 16px;">
      Or if you'd prefer, I can jump on a quick 10-minute call to walk you through it. Just reply to this email and let me know what time works for you.
    </p>
    
    <p style="font-size: 16px; color: #374151; margin-bottom: 4px;">Cheers,</p>
    <p style="font-size: 16px; color: #374151; font-weight: 600; margin: 0;">Anthony</p>
  </div>
  
  <!-- Footer -->
  <div style="background-color: #243665; padding: 20px; text-align: center; border-radius: 0 0 8px 8px;">
    <p style="color: rgba(255,255,255,0.8); font-size: 14px; margin: 0;">
      Pitchallo - Smart Pitch Management for Football Clubs
    </p>
    <p style="color: rgba(255,255,255,0.6); font-size: 12px; margin: 8px 0 0 0;">
      <a href="https://www.pitchallo.app" style="color: rgba(255,255,255,0.8); text-decoration: none;">www.pitchallo.app</a>
    </p>
  </div>
</div>`;

  const handleCopyEmail = () => {
    navigator.clipboard.writeText(emailBodyPlain);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  
  const handleCopyHtml = async () => {
    try {
      // Copy HTML to clipboard - works when pasting into rich text editors
      const blob = new Blob([emailBodyHtml], { type: 'text/html' });
      const clipboardItem = new ClipboardItem({ 'text/html': blob });
      await navigator.clipboard.write([clipboardItem]);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      // Fallback to plain text
      navigator.clipboard.writeText(emailBodyPlain);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };
  
  const handleOpenGmail = () => {
    const gmailUrl = `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(club.ownerEmail || '')}&su=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(emailBodyPlain)}`;
    window.open(gmailUrl, '_blank');
  };
  
  return (
    <div style={styles.clubRow}>
      <div style={styles.clubMain}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={styles.clubName}>
            {club.clubName || club.clubId}
          </div>
          {trialDays !== null && (
            <span style={{
              fontSize: '11px',
              padding: '2px 8px',
              borderRadius: '12px',
              fontWeight: '600',
              backgroundColor: trialDays <= 2 ? '#fef2f2' : trialDays <= 4 ? '#fefce8' : '#f0fdf4',
              color: trialDays <= 2 ? '#dc2626' : trialDays <= 4 ? '#ca8a04' : '#16a34a',
              border: `1px solid ${trialDays <= 2 ? '#fecaca' : trialDays <= 4 ? '#fde047' : '#bbf7d0'}`
            }}>
              {trialDays <= 0 ? 'Trial Expired' : `${trialDays}d left`}
            </span>
          )}
          {club.subscriptionStatus === 'active' && (
            <span style={{
              fontSize: '11px',
              padding: '2px 8px',
              borderRadius: '12px',
              fontWeight: '600',
              backgroundColor: '#dbeafe',
              color: '#2563eb',
              border: '1px solid #93c5fd'
            }}>
              Subscribed
            </span>
          )}
        </div>
        <div style={styles.clubMeta}>
          Signed up: {formatDate(club.signupAt)}
          {club.ownerEmail && (
            <span style={{ marginLeft: '8px', color: '#9ca3af' }}>
              ({club.ownerEmail})
            </span>
          )}
        </div>
      </div>
      
      <div style={styles.clubProgress}>
        <div style={styles.progressSteps}>
          <ProgressStep filled={true} label="Signup" />
          <ProgressConnector filled={club.firstPitchAt} />
          <ProgressStep 
            filled={club.firstPitchAt} 
            label="Pitch" 
            time={isLegacy ? null : formatMinutes(club.signupToPitchMinutes)} 
          />
          <ProgressConnector filled={club.firstTeamAt} />
          <ProgressStep 
            filled={club.firstTeamAt} 
            label="Team" 
            time={isLegacy ? null : formatMinutes(club.signupToTeamMinutes)} 
          />
          <ProgressConnector filled={club.firstAllocationAt} />
          <ProgressStep 
            filled={club.firstAllocationAt} 
            label="Allocation" 
            time={isLegacy ? null : formatMinutes(club.signupToAllocationMinutes)} 
          />
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        {/* Email button - only show for incomplete onboarding */}
        {!club.firstAllocationAt && club.ownerEmail && (
          <button
            onClick={() => setShowEmailDraft(!showEmailDraft)}
            style={{
              padding: '6px 10px',
              backgroundColor: '#f3f4f6',
              color: '#374151',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: '500'
            }}
          >
            ✉️ Email
          </button>
        )}
        
        <div style={{
          ...styles.statusBadge,
          backgroundColor: status.color + '20',
          color: status.color
        }}>
          {status.label}
        </div>
      </div>
      
      {/* Email Draft Modal */}
      {showEmailDraft && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}
        onClick={() => setShowEmailDraft(false)}
        >
          <div 
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '24px',
              maxWidth: '700px',
              width: '95%',
              maxHeight: '90vh',
              overflow: 'auto',
              boxShadow: '0 20px 50px rgba(0,0,0,0.3)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              marginBottom: '16px'
            }}>
              <h3 style={{ margin: 0, fontSize: '18px', color: '#111827' }}>
                Email Draft for {club.clubName}
              </h3>
              <button
                onClick={() => setShowEmailDraft(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#9ca3af'
                }}
              >
                ×
              </button>
            </div>
            
            <div style={{ marginBottom: '12px' }}>
              <strong>To:</strong> {club.ownerEmail}
            </div>
            <div style={{ marginBottom: '12px' }}>
              <strong>Subject:</strong> {emailSubject}
            </div>
            
            {/* HTML Preview */}
            <div style={{
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              overflow: 'hidden',
              marginBottom: '16px'
            }}>
              <div dangerouslySetInnerHTML={{ __html: emailBodyHtml }} />
            </div>
            
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={handleCopyEmail}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#f3f4f6',
                  color: '#374151',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                📋 Copy Plain Text
              </button>
              <button
                onClick={handleCopyHtml}
                style={{
                  padding: '10px 20px',
                  backgroundColor: copied ? '#22c55e' : '#f3f4f6',
                  color: copied ? 'white' : '#374151',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                {copied ? '✓ Copied!' : '📋 Copy HTML'}
              </button>
              <button
                onClick={handleOpenGmail}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#243665',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                📧 Open in Gmail
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Helper function for ClubRow
const getProgressStatus = (club) => {
  if (club.firstAllocationAt) return { label: 'Complete', color: '#22c55e' };
  if (club.firstTeamAt) return { label: 'Has Team', color: '#3b82f6' };
  if (club.firstPitchAt) return { label: 'Has Pitch', color: '#f59e0b' };
  return { label: 'Just Started', color: '#ef4444' };
};

// Progress Step Component
const ProgressStep = ({ filled, label, time }) => (
  <div style={styles.progressStep}>
    <div style={{
      ...styles.progressDot,
      backgroundColor: filled ? '#22c55e' : '#e5e7eb'
    }}>
      {filled && '✓'}
    </div>
    <div style={styles.progressLabel}>{label}</div>
    {time && time !== '—' && (
      <div style={styles.progressTime}>{time}</div>
    )}
  </div>
);

// Progress Connector Component
const ProgressConnector = ({ filled }) => (
  <div style={{
    ...styles.progressConnector,
    backgroundColor: filled ? '#22c55e' : '#e5e7eb'
  }} />
);

// Styles
const styles = {
  container: {
    padding: '24px',
    maxWidth: '1200px',
    margin: '0 auto',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    backgroundColor: '#f8fafc',
    minHeight: '100vh',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '32px',
    flexWrap: 'wrap',
    gap: '16px',
  },
  title: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#243665',
    margin: '0 0 4px 0',
  },
  subtitle: {
    fontSize: '14px',
    color: '#64748b',
    margin: 0,
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  lastRefresh: {
    fontSize: '12px',
    color: '#94a3b8',
  },
  refreshButton: {
    padding: '8px 16px',
    backgroundColor: '#243665',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
  },
  metricsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '16px',
    marginBottom: '32px',
  },
  metricCard: {
    backgroundColor: 'white',
    padding: '24px',
    borderRadius: '12px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    textAlign: 'center',
  },
  metricIcon: {
    fontSize: '32px',
    marginBottom: '8px',
  },
  metricValue: {
    fontSize: '32px',
    fontWeight: '700',
    color: '#243665',
    marginBottom: '4px',
  },
  metricLabel: {
    fontSize: '13px',
    color: '#64748b',
    fontWeight: '500',
  },
  metricSubtext: {
    fontSize: '11px',
    color: '#94a3b8',
    marginTop: '4px',
  },
  funnelSection: {
    backgroundColor: 'white',
    padding: '24px',
    borderRadius: '12px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    marginBottom: '24px',
  },
  sectionTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#1e293b',
    margin: '0 0 20px 0',
  },
  funnel: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  funnelRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  funnelLabel: {
    width: '120px',
    fontSize: '14px',
    fontWeight: '500',
    color: '#475569',
  },
  funnelBarContainer: {
    flex: 1,
    height: '24px',
    backgroundColor: '#f1f5f9',
    borderRadius: '4px',
    overflow: 'hidden',
  },
  funnelBarFill: {
    height: '100%',
    borderRadius: '4px',
    transition: 'width 0.5s ease',
  },
  funnelCount: {
    width: '80px',
    textAlign: 'right',
    fontSize: '14px',
    fontWeight: '600',
    color: '#1e293b',
  },
  funnelPercentage: {
    fontWeight: '400',
    color: '#94a3b8',
  },
  alertSection: {
    backgroundColor: '#fef2f2',
    border: '1px solid #fecaca',
    padding: '20px',
    borderRadius: '12px',
    marginBottom: '24px',
  },
  alertTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#dc2626',
    margin: '0 0 16px 0',
  },
  section: {
    backgroundColor: 'white',
    padding: '24px',
    borderRadius: '12px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    marginBottom: '24px',
  },
  clubList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  clubRow: {
    display: 'flex',
    alignItems: 'center',
    padding: '16px',
    backgroundColor: '#f8fafc',
    borderRadius: '8px',
    gap: '24px',
    flexWrap: 'wrap',
  },
  clubMain: {
    minWidth: '200px',
  },
  clubName: {
    fontSize: '15px',
    fontWeight: '600',
    color: '#1e293b',
    marginBottom: '4px',
  },
  clubMeta: {
    fontSize: '12px',
    color: '#64748b',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  methodBadge: {
    backgroundColor: '#e0e7ff',
    color: '#4f46e5',
    padding: '2px 6px',
    borderRadius: '4px',
    fontSize: '10px',
    fontWeight: '500',
  },
  clubProgress: {
    flex: 1,
    minWidth: '300px',
  },
  progressSteps: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '4px',
  },
  progressStep: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '4px',
    minWidth: '50px',
    minHeight: '60px',
  },
  progressDot: {
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '12px',
    color: 'white',
    fontWeight: '600',
  },
  progressLabel: {
    fontSize: '10px',
    color: '#64748b',
    fontWeight: '500',
  },
  progressTime: {
    fontSize: '10px',
    color: '#22c55e',
    fontWeight: '600',
  },
  progressConnector: {
    width: '40px',
    height: '3px',
    borderRadius: '2px',
    marginTop: '10px',
  },
  statusBadge: {
    padding: '6px 12px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: '600',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '80px 20px',
  },
  spinner: {
    width: '40px',
    height: '40px',
    border: '3px solid #e5e7eb',
    borderTopColor: '#243665',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  loadingText: {
    marginTop: '16px',
    color: '#64748b',
  },
  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '80px 20px',
  },
  errorIcon: {
    fontSize: '48px',
    marginBottom: '16px',
  },
  errorText: {
    color: '#dc2626',
    marginBottom: '16px',
  },
  retryButton: {
    padding: '10px 20px',
    backgroundColor: '#243665',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
  },
  emptyState: {
    textAlign: 'center',
    padding: '60px 20px',
    backgroundColor: 'white',
    borderRadius: '12px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  },
  emptyIcon: {
    fontSize: '48px',
    marginBottom: '16px',
  },
  emptyTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#1e293b',
    marginBottom: '8px',
  },
  emptyText: {
    fontSize: '14px',
    color: '#64748b',
  },
};

// Add keyframe animation for spinner
const styleSheet = document.createElement('style');
styleSheet.textContent = `
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;
document.head.appendChild(styleSheet);

export default OnboardingDashboard;
